// src/admin/pages/roles/role.ts

export interface Feature {
  code: string;
  name: string;
  group: string;
}

export interface Role {
  id?: string;
  name: string;
  description: string;
  permissions: string[]; // array of feature codes
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string;
}

export interface RoleFormData {
  name: string;
  description: string;
  permissions: string[]; // array of feature codes
}

// ─────────────────────────────────────────────────────────────────────────────
// HARDCODED FEATURES LIST
// Add / remove features here as your application grows.
// Each feature has a unique `code`, a display `name`, and a `group`
// so the UI can render them in logical sections.
// ─────────────────────────────────────────────────────────────────────────────
export const APP_FEATURES: Feature[] = [
  // Dashboard
  { code: 'dashboard.view', name: 'View Dashboard', group: 'Dashboard' },

  // Users
  { code: 'users.view', name: 'View Users', group: 'Users' },
  { code: 'users.edit', name: 'Edit User (Assign Role)', group: 'Users' },
  { code: 'users.toggle_status', name: 'Activate / Deactivate User', group: 'Users' },
  { code: 'users.delete', name: 'Delete User', group: 'Users' },
  { code: 'users.assign_role', name: 'Assign Role to User', group: 'Users' },

  // Roles
  { code: 'roles.view', name: 'View Roles', group: 'Roles' },
  { code: 'roles.create', name: 'Create Role', group: 'Roles' },
  { code: 'roles.edit', name: 'Edit Role', group: 'Roles' },
  { code: 'roles.delete', name: 'Delete Role', group: 'Roles' },

  // Orders
  { code: 'orders.view', name: 'View Orders', group: 'Orders' },
  { code: 'orders.edit', name: 'Edit Order', group: 'Orders' },
  { code: 'orders.create', name: 'Create Order', group: 'Orders' },
  { code: 'orders.delete', name: 'Delete Order', group: 'Orders' },
  { code: 'orders.invoice', name: 'Download Invoice', group: 'Orders' },
  { code: 'orders.return.view', name: 'View Order Returns', group: 'Orders' },
  { code: 'orders.return.manage', name: 'Manage Order Returns', group: 'Orders' },

  // Products
  { code: 'products.view', name: 'View Products', group: 'Products' },
  { code: 'products.create', name: 'Create Product', group: 'Products' },
  { code: 'products.edit', name: 'Edit Product', group: 'Products' },
  { code: 'products.delete', name: 'Delete Product', group: 'Products' },

  // Category
  { code: 'category.view', name: 'View Categories', group: 'Category' },
  { code: 'category.create', name: 'Create Category', group: 'Category' },
  { code: 'category.edit', name: 'Edit Category', group: 'Category' },
  { code: 'category.delete', name: 'Delete Category', group: 'Category' },

  // Variant
  { code: 'variant.view', name: 'View Variants', group: 'Variant' },
  { code: 'variant.create', name: 'Create Variant', group: 'Variant' },
  { code: 'variant.edit', name: 'Edit Variant', group: 'Variant' },
  { code: 'variant.delete', name: 'Delete Variant', group: 'Variant' },

  // Brand
  { code: 'brand.view', name: 'View Brands', group: 'Brand' },
  { code: 'brand.create', name: 'Create Brand', group: 'Brand' },
  { code: 'brand.edit', name: 'Edit Brand', group: 'Brand' },
  { code: 'brand.delete', name: 'Delete Brand', group: 'Brand' },

  // Warehouse
  { code: 'warehouse.view', name: 'View Warehouses', group: 'Warehouse' },
  { code: 'warehouse.create', name: 'Create Warehouse', group: 'Warehouse' },
  { code: 'warehouse.edit', name: 'Edit Warehouse', group: 'Warehouse' },
  { code: 'warehouse.delete', name: 'Delete Warehouse', group: 'Warehouse' },
  { code: 'warehouse.transfer', name: 'Transfer Warehouse Stock', group: 'Warehouse' },

  // Shipping
  { code: 'shipping.view', name: 'View Shipping', group: 'Shipping' },
  { code: 'shipping.create', name: 'Create Shipping', group: 'Shipping' },
  { code: 'shipping.edit', name: 'Edit Shipping', group: 'Shipping' },
  { code: 'shipping.delete', name: 'Delete Shipping', group: 'Shipping' },

  // Purchase Orders
  { code: 'purchase_order.view', name: 'View Purchase Orders', group: 'Purchase Orders' },
  { code: 'purchase_order.create', name: 'Create Purchase Order', group: 'Purchase Orders' },
  { code: 'purchase_order.edit', name: 'Edit Purchase Order', group: 'Purchase Orders' },
  { code: 'purchase_order.delete', name: 'Delete Purchase Order', group: 'Purchase Orders' },
  { code: 'purchase_order.submit', name: 'Submit PO for Approval', group: 'Purchase Orders' }, // ADD
  { code: 'purchase_order.approve', name: 'Approve / Cancel PO', group: 'Purchase Orders' }, // ADD
  { code: 'purchase_order.order', name: 'Mark PO as Ordered', group: 'Purchase Orders' }, // ADD
  { code: 'purchase_order.receive', name: 'Mark PO as Received', group: 'Purchase Orders' }, // ADD
  { code: 'purchase_order.payment', name: 'Record PO Payment', group: 'Purchase Orders' },

  // Inventory Return
  { code: 'inventory_return.view', name: 'View PO Returns', group: 'Inventory Return' },
  { code: 'inventory_return.manage', name: 'Manage PO Returns', group: 'Inventory Return' },

  // Supplier
  { code: 'supplier.view', name: 'View Suppliers', group: 'Supplier' },
  { code: 'supplier.create', name: 'Create Supplier', group: 'Supplier' },
  { code: 'supplier.edit', name: 'Edit Supplier', group: 'Supplier' },
  { code: 'supplier.delete', name: 'Delete Supplier', group: 'Supplier' },

  // Reports
  { code: 'reports.view', name: 'View Reports', group: 'Reports' },
  { code: 'reports.create', name: 'Generate Report', group: 'Reports' }, // ADD
  { code: 'reports.delete', name: 'Delete Report', group: 'Reports' }, // ADD
  { code: 'reports.download', name: 'Download Report', group: 'Reports' }, // ADD

  // Messages
  { code: 'messages.view', name: 'View Messages', group: 'Messages' },
  { code: 'messages.reply', name: 'Reply to Messages', group: 'Messages' },
  { code: 'messages.delete', name: 'Delete Messages', group: 'Messages' },

  // Reviews
  { code: 'reviews.view', name: 'View Reviews', group: 'Reviews' },
  { code: 'reviews.manage', name: 'Manage Reviews', group: 'Reviews' },

  // Blog
  { code: 'blog.view', name: 'View Blogs', group: 'Blog' },
  { code: 'blog.create', name: 'Create Blog', group: 'Blog' },
  { code: 'blog.edit', name: 'Edit Blog', group: 'Blog' },
  { code: 'blog.delete', name: 'Delete Blog', group: 'Blog' },
  { code: 'blog.publish', name: 'Publish / Unpublish Blog', group: 'Blog' },
  { code: 'blog.comments.manage', name: 'Manage Blog Comments', group: 'Blog' },

  // Newsletter
  { code: 'newsletter.view', name: 'View Newsletter', group: 'Newsletter' },
  { code: 'newsletter.manage', name: 'Manage Newsletter', group: 'Newsletter' },

  // Banner
  { code: 'banner.view', name: 'View Banner', group: 'Banner' },
  { code: 'banner.create', name: 'Upload Banner', group: 'Banner' },
  { code: 'banner.edit', name: 'Replace Banner', group: 'Banner' },
  { code: 'banner.delete', name: 'Delete Banner', group: 'Banner' },

  // Settings
  { code: 'settings.view', name: 'View Settings', group: 'Settings' },
  { code: 'settings.edit', name: 'Edit Settings', group: 'Settings' },
];

// Derive unique groups in insertion order
export const FEATURE_GROUPS: string[] = Array.from(
  new Set(APP_FEATURES.map((f) => f.group))
);